import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const CREDIT_RANGES = ["Excellent (740+)", "Good (700–739)", "Fair (640–699)", "Building Credit (<640)"];

export default function FinancingDialog({ car, trigger }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    desired_amount: "",
    annual_income: "",
    credit_range: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        type: "financing",
        name: form.name,
        email: form.email,
        phone: form.phone,
        car_id: car?.id,
        desired_amount: form.desired_amount ? parseFloat(form.desired_amount) : null,
        annual_income: form.annual_income ? parseFloat(form.annual_income) : null,
        credit_range: form.credit_range,
        message: form.message,
      });
      toast.success("Financing inquiry sent. A team member will reach out shortly.");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", desired_amount: "", annual_income: "", credit_range: "", message: "" });
    } catch (err) {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="financing-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-primary">Financing Inquiry</DialogTitle>
          <DialogDescription>
            Tell us a bit about your situation. We work with families of every credit story.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="fin-name">Full Name</Label>
              <Input
                id="fin-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="financing-name-input"
              />
            </div>
            <div>
              <Label htmlFor="fin-phone">Phone</Label>
              <Input
                id="fin-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="financing-phone-input"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="fin-email">Email</Label>
            <Input
              id="fin-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="financing-email-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="fin-amount">Loan Amount ($)</Label>
              <Input
                id="fin-amount"
                type="number"
                value={form.desired_amount}
                onChange={(e) => setForm({ ...form, desired_amount: e.target.value })}
                data-testid="financing-amount-input"
              />
            </div>
            <div>
              <Label htmlFor="fin-income">Annual Income ($)</Label>
              <Input
                id="fin-income"
                type="number"
                value={form.annual_income}
                onChange={(e) => setForm({ ...form, annual_income: e.target.value })}
                data-testid="financing-income-input"
              />
            </div>
          </div>
          <div>
            <Label>Credit Range</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between mt-1 font-normal"
                  data-testid="financing-credit-trigger"
                >
                  <span className={form.credit_range ? "" : "text-muted-foreground"}>
                    {form.credit_range || "Select your credit range"}
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
                {CREDIT_RANGES.map((c) => (
                  <DropdownMenuItem
                    key={c}
                    onSelect={() => setForm({ ...form, credit_range: c })}
                    data-testid={`financing-credit-option-${c.split(" ")[0].toLowerCase()}`}
                  >
                    {c}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div>
            <Label htmlFor="fin-message">Notes</Label>
            <Textarea
              id="fin-message"
              rows={3}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="financing-message-input"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} data-testid="financing-submit">
              {submitting ? "Submitting…" : "Send Financing Request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
